// src/utils/lineman.utils.ts
import {
	CENTER_X,
	LEFT_HASH_INNER_EDGE,
	LINEMAN_Y,
	RIGHT_HASH_INNER_EDGE,
	SPACING_CENTER_TO_CENTER,
} from '@/constants'
import type { HashAlignment } from '@/types'
import type { Player } from '@/types/Player.types'

const LINEMAN_LABELS = ['LT', 'LG', 'C', 'RG', 'RT']
const DEFAULT_LINEMAN_COLOR = '#FFFFFF'

/**
 * Get the center X position based on hash alignment
 */
export function getCenterXForHash(hashAlignment: HashAlignment): number {
	switch (hashAlignment) {
		case 'left':
			return LEFT_HASH_INNER_EDGE
		case 'right':
			return RIGHT_HASH_INNER_EDGE
		default:
			return CENTER_X
	}
}

/**
 * Create the default 5 offensive linemen for a new play
 */
export function createDefaultLinemen(hashAlignment: HashAlignment = 'middle'): Player[] {
	const centerX = getCenterXForHash(hashAlignment)

	return LINEMAN_LABELS.map((label, index) => {
		const offsetFromCenter = (index - 2) * SPACING_CENTER_TO_CENTER
		return {
			id: crypto.randomUUID(),
			x: centerX + offsetFromCenter,
			y: LINEMAN_Y,
			label,
			color: DEFAULT_LINEMAN_COLOR,
			isLineman: true,
		}
	})
}

/**
 * Reposition all linemen to a new hash alignment
 * Returns updated players array with linemen moved
 */
export function repositionLinemenForHash(
	players: Player[],
	newHashAlignment: HashAlignment,
): Player[] {
	const newCenterX = getCenterXForHash(newHashAlignment)

	return players.map((player) => {
		if (!player.isLineman) return player

		// Find lineman index from label to calculate offset
		const labelIndex = LINEMAN_LABELS.indexOf(player.label)
		if (labelIndex === -1) return player

		const offsetFromCenter = (labelIndex - 2) * SPACING_CENTER_TO_CENTER
		return {
			...player,
			x: newCenterX + offsetFromCenter,
		}
	})
}

/**
 * Check if all linemen are at their default positions for the given hash alignment
 * Returns true if all linemen are positioned correctly, false otherwise
 */
export function areLinemenAtDefaultPositions(
	players: Player[],
	hashAlignment: HashAlignment,
): boolean {
	const centerX = getCenterXForHash(hashAlignment)
	const linemen = players.filter(p => p.isLineman)

	for (const player of linemen) {
		const labelIndex = LINEMAN_LABELS.indexOf(player.label)
		if (labelIndex === -1) continue

		const expectedX = centerX + (labelIndex - 2) * SPACING_CENTER_TO_CENTER
		const expectedY = LINEMAN_Y

		// Use small tolerance for floating point comparison
		if (Math.abs(player.x - expectedX) > 0.1 || Math.abs(player.y - expectedY) > 0.1) {
			return false
		}
	}
	return true
}
